import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/components/ui/use-toast';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  emoji: string;
  badge_image_url?: string;
  points_required: number;
}

export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_id: string;
  unlocked_at: string;
  achievement?: Achievement;
}

export const useAchievements = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Obtener todos los logros disponibles
  const { data: achievements = [] } = useQuery<Achievement[]>({
    queryKey: ['achievements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('community_achievements')
        .select('*')
        .order('points_required', { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  // Obtener logros del usuario
  const { data: userAchievements = [] } = useQuery<UserAchievement[]>({
    queryKey: ['user-achievements', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('user_achievements')
        .select(`
          id,
          user_id,
          achievement_id,
          unlocked_at,
          achievement:community_achievements!achievement_id (
            name,
            description,
            emoji,
            badge_image_url,
            points_required
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      console.log('User achievements loaded:', data);
      return data;
    },
    enabled: !!user?.id,
  });

  // Verificar si el usuario ha desbloqueado un logro
  const checkAchievement = async (achievementId: string): Promise<boolean> => {
    if (!user?.id) return false;

    const { data } = await supabase
      .from('user_achievements')
      .select('id')
      .eq('user_id', user.id)
      .eq('achievement_id', achievementId)
      .single();

    return !!data;
  };

  // Desbloquear un nuevo logro
  const unlockAchievement = useMutation({
    mutationFn: async (achievementId: string) => {
      if (!user?.id) throw new Error('Usuario no autenticado');

      const alreadyUnlocked = await checkAchievement(achievementId);
      if (alreadyUnlocked) return;

      const { error } = await supabase
        .from('user_achievements')
        .insert({
          user_id: user.id,
          achievement_id: achievementId,
        });

      if (error) throw error;

      // Obtener detalles del logro para la notificación
      const achievement = achievements.find(a => a.id === achievementId);
      if (achievement) {
        toast({
          title: "¡Nuevo Logro Desbloqueado! " + achievement.emoji,
          description: achievement.description,
          duration: 5000,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-achievements', user?.id]);
    },
  });

  return {
    achievements,
    userAchievements,
    checkAchievement,
    unlockAchievement,
    hasAchievement: (achievementId: string) => 
      userAchievements.some(ua => ua.achievement_id === achievementId),
  };
};
